import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Users, GraduationCap, Landmark, Handshake, Building2, Network, Sparkles, CheckCircle, Calendar, BadgeCheck, Globe2, Target, Lightbulb, HeartHandshake, ChevronDown, ChevronUp, Mail, Phone, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

// ================= SafeLink helpers (hash-safe smooth scroll) =================
function extractHashId(href) {
  if (!href || href[0] !== '#') return null;
  const after = href.slice(1);
  const id = after.split(/[?&]/)[0].trim();
  return id || null;
}
const SafeLink = ({ href, className = "", children, onClick=(e: any)=>{} }) => {
  const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  const handleClick = (e: any) => {
    e.preventDefault();
    onClick && onClick(e);
    if (href.startsWith('#')) {
      const id = extractHashId(href);
      if (id) {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.location.hash = href; // keep hash for history
    }
  };
  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// ================= Shared UI =================
const Section = ({ id="", className = "", children }) => (
  <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);
const Card = ({ className = "", children }) => (
  <div className={`rounded-2xl shadow-lg bg-white/90 backdrop-blur border border-slate-100 ${className}`}>{children}</div>
);
const H2 = ({ children, kicker, center }) => (
  <div className={`mb-8 ${center ? "text-center" : ""}`}>
    {kicker && <div className="mb-2 text-sm font-semibold tracking-widest uppercase text-emerald-600">{kicker}</div>}
    <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{children}</h2>
  </div>
);
const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur">
    {children}
  </span>
);
const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-900 px-3 py-1 rounded-full">
    {children}
  </span>
);

// ================= Data =================
const subTracks = [
  {
    key: "trainer",
    title: "Trainer Track",
    icon: GraduationCap,
    color: "from-amber-500 to-orange-600",
    objectives: [
      "Transfer DPI & platform-business knowledge at scale",
      "Enable learning\u2192earning pathways",
      "Build local capacity via workshops & certifications",
    ],
    responsibilities: [
      "Conduct workshops, bootcamps, accelerator sessions (online/offline)",
      "Author & deliver LMS courses (DIGIIT or co-branded)",
      "Localize DPI concepts for MSMEs, SHGs, youth",
      "Co-host events with partners across regions/sectors",
    ],
  },
  {
    key: "mentor",
    title: "Mentor Track",
    icon: HeartHandshake,
    color: "from-purple-600 to-pink-600",
    objectives: [
      "Provide strategic direction & personal guidance",
      "Create trusted support across Venture tiers",
      "Foster peer & inter-generational learning",
    ],
    responsibilities: [
      "1:1 mentorship for Community, Practitioners & Platformpreneurs",
      "Join structured programs (incubations, scale camps)",
      "Specialize by domain (commerce, agri, fintech, logistics, education)",
      "Panels, fireside chats, learning circles",
    ],
  },
  {
    key: "corporate",
    title: "Corporate Track",
    icon: Building2,
    color: "from-emerald-600 to-teal-600",
    objectives: [
      "Drive CSR-linked impact & inclusion",
      "Create jobs, internships & real-world exposure",
      "Enable procurement, pilots & partnerships",
    ],
    responsibilities: [
      "Sponsor bootcamps, POCs, seed grants, growth initiatives",
      "Post internships & jobs for digitally-skilled youth",
      "Offer corporate mentorship & business challenges",
      "Co-develop industry tracks with DPI (e.g., ONDC + ULIP)",
    ],
  },
];

const benefits = [
  { title: "Ecosystem Backbone", desc: "Catalysts strengthen learning, mentorship & business support layers.", icon: Landmark },
  { title: "Trust & Quality", desc: "Standards, certification, and vetted programs ensure continuity.", icon: BadgeCheck },
  { title: "Network Effects", desc: "Route knowledge, capital, and opportunities across tracks.", icon: Network },
];

const events = [
  { date: "2025-09-06", title: "Catalyst Onboarding Clinic (Virtual)", tag: "Catalyst" },
  { date: "2025-09-18", title: "Trainer Track: DPI 101 Train-the-Trainer", tag: "Trainer" },
  { date: "2025-09-25", title: "Mentor Circle: MSME Growth Playbooks", tag: "Mentor" },
  { date: "2025-10-03", title: "Corporate CSR Roundtable: Women & DPI", tag: "Corporate" },
];

// ================= NAV =================
// const Nav = () => {
//   const items = [
//     { href: "#home", label: "Home" },
//     { href: "#subtracks", label: "Sub‑Tracks" },
//     { href: "#how", label: "How it Works" },
//     { href: "#benefits", label: "Benefits" },
//     { href: "#events", label: "Events" },
//     { href: "#join", label: "Join" },
//     { href: "#faq", label: "FAQ" },
//     { href: "#contact", label: "Contact" },
//   ];
//   const [open, setOpen] = useState(false);
//   return (
//     <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
//       <Section className="flex h-16 items-center justify-between">
//         <SafeLink href="#home" className="flex items-center gap-2 font-extrabold text-slate-900 text-lg">
//           <span className="inline-grid place-items-center w-8 h-8 rounded-xl bg-emerald-600 text-white font-black">C</span>
//           Catalyst
//         </SafeLink>
//         <nav className="hidden md:flex items-center gap-6 text-sm">
//           {items.map(i => (
//             <SafeLink key={i.href} href={i.href} className="text-slate-600 hover:text-slate-900">{i.label}</SafeLink>
//           ))}
//           <SafeLink href="#join" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700">Get Involved <ArrowRight className="w-4 h-4"/></SafeLink>
//         </nav>
//         <button className="md:hidden p-2" onClick={()=>setOpen(v=>!v)} aria-label="Toggle menu">
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
//           <span className="block w-6 h-0.5 bg-slate-900"></span>
//         </button>
//       </Section>
//       {open && (
//         <div className="md:hidden border-t border-slate-200 bg-white">
//           <Section className="py-4 flex flex-col gap-3">
//             {items.map(i => (
//               <SafeLink key={i.href} href={i.href} onClick={()=>setOpen(false)} className="text-slate-700">{i.label}</SafeLink>
//             ))}
//             <SafeLink href="#join" onClick={()=>setOpen(false)} className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700">Get Involved <ArrowRight className="w-4 h-4"/></SafeLink>
//           </Section>
//         </div>
//       )}
//     </header>
//   );
// };

const Nav = () => {
  const submenus = [
    // { href: "#home", label: "Home"},
    { href: "#subtracks", label: "Sub‑Tracks" },
    { href: "#how", label: "How it Works" },
    { href: "#benefits", label: "Benefits" },
    { href: "#events", label: "Events" },
    { href: "#join", label: "Join" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];

  const [openMobile, setOpenMobile] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-slate-200">
          <Section id="one-digiit" className="flex flex-row h-12 w-full items-center justify-right">
            {/* Second row: submenus (desktop only) */}
            <div className="hidden md:block flex flex-row w-full items-center justify-right">
              <Section
                id="nav-sub"
                className="flex items-center gap-4 py-2 overflow-x-auto justify-end"
              >
                {submenus.map((i) => (
                  <SafeLink
                    key={i.href}
                    href={i.href}
                    className="text-sm text-slate-600 hover:text-slate-900 whitespace-nowrap"
                  >
                    {i.label}
                  </SafeLink>
                ))}
    
                {/* CTA */}
                <SafeLink
                  href="#contact"
                  className="hidden md:inline-flex items-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                >
                  Talk to Us <ArrowRight className="w-4 h-4" />
                </SafeLink>
              </Section>
            </div>
    
    
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setOpenMobile((v) => !v)}
              aria-label="Toggle menu"
            >
              <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
              <span className="block w-6 h-0.5 bg-slate-900 mb-1"></span>
              <span className="block w-6 h-0.5 bg-slate-900"></span>
            </button>
          </Section>
    
          {/* Mobile dropdown */}
          {openMobile && (
            <div className="md:hidden border-t border-slate-200 bg-white">
              <Section id={''} className="py-4 flex flex-col gap-3">
                <div className="mt-4 border-t border-slate-200 pt-4">
                  {submenus.map((i) => (
                    <SafeLink
                      key={i.href}
                      href={i.href}
                      onClick={() => setOpenMobile(false)}
                      className="text-slate-600"
                    >
                      {i.label}
                    </SafeLink>
                  ))}
                </div>
                <SafeLink
                  href="#contact"
                  onClick={() => setOpenMobile(false)}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-600"
                >
                  Talk to Us <ArrowRight className="w-4 h-4" />
                </SafeLink>
              </Section>
            </div>
          )}
        </header>
  );
};

// ================= HERO =================
const Hero = () => (
  <div id="home" className="relative overflow-hidden bg-gradient-to-b from-emerald-700 via-emerald-700 to-teal-700 text-white">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(255,255,255,0.06),transparent_40%)]"/>
    <Section className="relative py-16 md:py-24">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}}>
          <div className="flex gap-2 mb-4">
            <Badge>Ecosystem Enabler</Badge>
            <Badge>DPI‑led</Badge>
            <Badge>Nation‑building</Badge>
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-tight">Catalyst Track — Empowering the Enablers</h1>
          <p className="mt-4 text-lg md:text-xl text-slate-200 max-w-2xl">Catalysts support, amplify, and sustain the growth of entrepreneurs and ventures across the ecosystem — through education, mentorship, corporate collaboration, and impact programs.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <SafeLink href="#subtracks" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-5 py-3 font-semibold">Explore Sub‑Tracks <ArrowRight className="w-4 h-4"/></SafeLink>
            <SafeLink href="#join" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-5 py-3">Become a Catalyst</SafeLink>
          </div>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-slate-300">
            <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4"/> Clinics every month</span>
            <span className="inline-flex items-center gap-2"><Users className="w-4 h-4"/> Peer & expert network</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="w-4 h-4"/> Certification options</span>
          </div>
        </motion.div>
        <motion.div initial={{opacity:0, y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6, delay:0.1}}>
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Pill><Sparkles className="w-4 h-4"/> Roles</Pill>
              <span className="text-slate-500">Where you fit</span>
            </div>
            <ul className="grid sm:grid-cols-2 gap-4">
              {subTracks.map(st => (
                <li key={st.key} className="rounded-xl p-4 border border-slate-200 hover:shadow-md transition">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${st.color} grid place-items-center text-white mb-2`}>
                    {React.createElement(st.icon, { className: "w-5 h-5" })}
                  </div>
                  <div className="font-semibold text-white md:text-slate-900 md:text-inherit">{st.title}</div>
                  <div className="mt-1 text-xs text-slate-300 md:text-slate-600">Enable success across the venture & partner tracks.</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 text-xs text-slate-500">More sub‑tracks coming soon: Funder, Academia, Tech, Policy, Media.</div>
          </Card>
        </motion.div>
      </div>
    </Section>
  </div>
);

// ================= SUB‑TRACKS =================
const SubTracks = () => (
  <Section id="subtracks" className="py-16">
    <H2 kicker="Tracks" center>Specialized roles that nurture the ecosystem</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {subTracks.map(st => (
        <motion.div key={st.key} initial={{opacity:0, y:12}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.35}}>
          <Card className="p-6 h-full flex flex-col">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${st.color} grid place-items-center text-white`}>
                {React.createElement(st.icon, { className: "w-5 h-5" })}
              </div>
              <div className="text-xl font-bold text-slate-900">{st.title}</div>
            </div>
            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-700">Objectives</div>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                {st.objectives.map((o, i) => <li key={i} className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/>{o}</li>)}
              </ul>
            </div>
            <div className="mt-4">
              <div className="text-sm font-semibold text-slate-700">Key responsibilities</div>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                {st.responsibilities.map((r, i) => <li key={i} className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/>{r}</li>)}
              </ul>
            </div>
            <div className="mt-5 flex gap-3 mt-auto">
              <SafeLink href={`#join?track=${st.key}`} className="rounded-full px-4 py-2 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">Apply to {st.title}</SafeLink>
              <SafeLink href="#faq" className="rounded-full px-4 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Learn more</SafeLink>
            </div>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

// ================= HOW IT WORKS =================
const HowItWorks = () => (
  <Section id="how" className="py-16">
    <H2 kicker="How it Works" center>{`From intent \u2192 onboarding \u2192 impact`}</H2>
    <div className="relative">
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-emerald-200 hidden md:block"/>
      <ol className="space-y-6">
        {[
          { title: "Apply", desc: "Tell us your domain, region, and how you’ll enable others.", icon: Target },
          { title: "Onboard", desc: "Get playbooks, templates, and certification options.", icon: BadgeCheck },
          { title: "Activate", desc: "Run workshops, mentoring circles, CSR pilots.", icon: Sparkles },
          { title: "Measure", desc: "Impact dashboards: learners, jobs, pilots, revenue.", icon: Lightbulb },
        ].map((s, idx) => (
          <li key={s.title} className="grid md:grid-cols-2 gap-4 items-start">
            <div className="md:text-right">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700">Step {idx+1}</div>
              <div className="text-xl font-bold text-slate-900">{s.title}</div>
            </div>
            <Card className="p-5 relative">
              <div className="absolute -left-3 top-5 hidden md:block w-6 h-6 rounded-full bg-white border-2 border-emerald-500"/>
              {React.createElement(s.icon, { className: "w-6 h-6 text-emerald-600" })}
              <div className="mt-2 text-sm text-slate-700">{s.desc}</div>
            </Card>
          </li>
        ))}
      </ol>
    </div>
  </Section>
);

// ================= BENEFITS =================
const Benefits = () => (
  <Section id="benefits" className="py-16">
    <H2 kicker="Why Catalysts" center>Quality, trust, and continuity across programs</H2>
    <div className="grid md:grid-cols-3 gap-6">
      {benefits.map((b) => (
        <Card key={b.title} className="p-6 text-center">
          {React.createElement(b.icon, { className: "w-10 h-10 text-emerald-600 mx-auto" })}
          <div className="mt-3 font-semibold text-slate-900">{b.title}</div>
          <div className="text-sm text-slate-600">{b.desc}</div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= EVENTS =================
const Events = () => (
  <Section id="events" className="py-16">
    <H2 kicker="Events" center>Upcoming catalyst clinics & meetups</H2>
    <div className="grid md:grid-cols-2 gap-4">
      {events.map(e => (
        <Card key={e.title} className="p-5">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Calendar className="w-4 h-4"/> {new Date(e.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
          <div className="mt-2 font-semibold text-slate-900">{e.title}</div>
          <div className="mt-3 flex gap-2">
            <SafeLink href="#join" className="rounded-full px-3 py-1.5 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700">RSVP</SafeLink>
            <SafeLink href="#faq" className="rounded-full px-3 py-1.5 text-sm font-semibold border border-slate-300 hover:bg-slate-50">Details</SafeLink>
          </div>
        </Card>
      ))}
    </div>
  </Section>
);

// ================= JOIN FORM =================
const Join = () => {
  const [form, setForm] = useState({ name:"", email:"", phone:"", city:"", affiliation:"", track:"trainer", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = (e) => {
    e.preventDefault();
    if(!form.name || !form.email) { alert("Please fill name & email"); return; }
    setSubmitted(true);
  };
  return (
    <div id="join" className="bg-gradient-to-b from-slate-50 to-white border-t border-slate-200">
      <Section className="py-16">
        <H2 kicker="Join" center>Become a Catalyst</H2>
        <div className="grid lg:grid-cols-2 gap-8">
          <Card className="p-6 md:p-8">
            <div className="text-sm text-slate-600">Tell us how you’d like to enable the ecosystem. We’ll review and invite you to the next onboarding clinic.</div>
            <form onSubmit={onSubmit} className="mt-6 grid grid-cols-1 gap-4">
              <input name="name" value={form.name} onChange={onChange} placeholder="Full name" className="rounded-xl border border-slate-300 px-3 py-2 outline-none"/>
              <input name="email" value={form.email} onChange={onChange} placeholder="Email" className="rounded-xl border border-slate-300 px-3 py-2 outline-none"/>
              <div className="grid grid-cols-2 gap-4">
                <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone" className="rounded-xl border border-slate-300 px-3 py-2 outline-none"/>
                <input name="city" value={form.city} onChange={onChange} placeholder="City / Region" className="rounded-xl border border-slate-300 px-3 py-2 outline-none"/>
              </div>
              <input name="affiliation" value={form.affiliation} onChange={onChange} placeholder="Affiliation (org/institution)" className="rounded-xl border border-slate-300 px-3 py-2 outline-none"/>
              <select name="track" value={form.track} onChange={onChange} className="rounded-xl border border-slate-300 px-3 py-2 outline-none">
                <option value="trainer">Trainer Track</option>
                <option value="mentor">Mentor Track</option>
                <option value="corporate">Corporate Track</option>
              </select>
              <textarea name="message" value={form.message} onChange={onChange} placeholder="Tell us about your domain, experience, and ambition" className="rounded-xl border border-slate-300 px-3 py-2 outline-none min-h-[120px]"/>
              <button type="submit" className="rounded-full px-5 py-3 bg-emerald-600 text-white font-semibold hover:bg-emerald-700">Submit Application</button>
              {submitted && <div className="text-sm text-emerald-700">Thanks! We’ll reach out with the next steps.</div>}
            </form>
          </Card>
          <div className="space-y-6">
            <Card className="p-6">
              <div className="font-semibold text-slate-900">What we look for</div>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/> Proven expertise (domain or pedagogy)</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/> Commitment to enablement (not just services)</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/> Regional reach or community access</li>
                <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5"/> Alignment with DPI mission & outcomes</li>
              </ul>
            </Card>
            <Card className="p-6">
              <div className="font-semibold text-slate-900">Ecosystem Connections</div>
              <div className="text-sm text-slate-600">Catalysts plug into Communities (chapters & guilds), the Entrepreneurship Development Academy (curriculum & certs), and DIGIIT Cloud (platforms & rails) for real outcomes.</div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <Pill><Globe2 className="w-4 h-4"/> Communities</Pill>
                <Pill><GraduationCap className="w-4 h-4"/> Academy</Pill>
                <Pill><Building2 className="w-4 h-4"/> DIGIIT.AI</Pill>
              </div>
            </Card>
          </div>
        </div>
      </Section>
    </div>
  );
};

// ================= FAQ =================
const FAQ = () => {
  const items = [
    { q: "Who is a Catalyst?", a: "An enabler who helps others succeed — trainer, mentor, or corporate partner driving opportunities and impact." },
    { q: "Is there a fee to join?", a: "No. Joining is free. Some certifications or advanced programs may have fees or CSR sponsorships." },
    { q: "Do Catalysts need to run ventures?", a: "Not necessarily. Many Catalysts enable others through education, mentorship, and institutional support." },
    { q: "What tracks are coming next?", a: "Funder, Academia, Tech, Policy, and Media tracks are in the roadmap." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="py-16">
      <H2 kicker="FAQ" center>Answers to common questions</H2>
      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((f, i) => (
          <Card key={f.q} className="p-4">
            <button className="w-full flex items-center justify-between text-left" onClick={()=>setOpen(i===open? -1 : i)} aria-expanded={open===i}>
              <span className="font-semibold text-slate-900">{f.q}</span>
              {open===i ? <ChevronUp className="w-5 h-5 text-slate-500"/> : <ChevronDown className="w-5 h-5 text-slate-500"/>}
            </button>
            {open===i && <div className="mt-2 text-sm text-slate-600">{f.a}</div>}
          </Card>
        ))}
      </div>
    </Section>
  );
};

// ================= CONTACT =================
const Contact = () => (
  <div id="contact" className="bg-slate-900 text-slate-100">
    <Section className="py-12">
      <div className="grid md:grid-cols-3 gap-6 items-start">
        <div>
          <div className="text-xl font-bold">Catalyst Program Office</div>
          <div className="mt-2 text-sm text-slate-300">Reach out for partnerships, CSR collaboration, or to host a clinic.</div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2"><Mail className="w-4 h-4"/> catalyst@digiit.ai</div>
          <div className="flex items-center gap-2"><Phone className="w-4 h-4"/> +91‑00000‑00000</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4"/> Coimbatore • Chennai • Bengaluru</div>
        </div>
        <div className="text-right text-xs text-slate-400">© {new Date().getFullYear()} DIGIIT • ONE Network</div>
      </div>
    </Section>
  </div>
);

// ================= DEV DIAGNOSTICS (lightweight runtime tests) =================
const DevDiagnostics = () => {
  useEffect(() => {
    try {
      const how = document.getElementById('how');
      console.assert(!!how, 'TEST: #how section should exist');
      const heading = how?.querySelector('h2')?.textContent || '';
      const arrow = String.fromCharCode(0x2192);
      console.assert(heading.includes('intent') && heading.includes('impact'), 'TEST: H2 should contain intent and impact');
      console.assert('\u2192'.length === 6 && arrow === '\u2192'.replace('\\u', '\\u'), 'TEST: Unicode arrow escape sanity');
      console.assert(Array.isArray(subTracks) && subTracks.length >= 3, 'TEST: at least 3 subTracks');
    } catch (err) {
      console.warn('Diagnostics error', err);
    }
  }, []);
  return null;
};

// ================= PAGE =================
export default function CatalystPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Nav/>
      <Hero/>
      <SubTracks/>
      <HowItWorks/>
      <Benefits/>
      <Events/>
      <Join/>
      <FAQ/>
      <Contact/>
      <DevDiagnostics/>
    </div>
  );
}
